import React, { useState, useMemo } from 'react';
import { Repository } from '../../types/api';
import { RepositoryCard } from './RepositoryCard';
import { RepositoryFilters, FilterState } from './RepositoryFilters';
import { Loader2, AlertCircle } from 'lucide-react';

interface RepositoryListProps {
  repositories: Repository[];
  loading?: boolean;
  error?: string;
  onSync?: (fullName: string) => void;
  onSelectRepository?: (repository: Repository) => void;
}

export function RepositoryList({
  repositories,
  loading = false,
  error,
  onSync,
  onSelectRepository,
}: RepositoryListProps) {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    tags: [],
    language: '',
    hasFeatures: false,
  });

  // Extract unique tags and languages
  const { availableTags, availableLanguages } = useMemo(() => {
    const tags = new Set<string>();
    const languages = new Set<string>();

    repositories.forEach((repo) => {
      repo.tags?.forEach((tag) => tags.add(tag));
      if (repo.metadata?.language) {
        languages.add(repo.metadata.language);
      }
    });

    return {
      availableTags: Array.from(tags).sort(),
      availableLanguages: Array.from(languages).sort(),
    };
  }, [repositories]);

  // Filter repositories
  const filteredRepositories = useMemo(() => {
    return repositories.filter((repo) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const fullName = `${repo.owner}/${repo.name}`.toLowerCase();
        if (!fullName.includes(searchLower)) {
          return false;
        }
      }

      // Tags filter
      if (filters.tags.length > 0) {
        const repoTags = repo.tags || [];
        const hasAllTags = filters.tags.every((tag) => repoTags.includes(tag));
        if (!hasAllTags) {
          return false;
        }
      }

      // Language filter
      if (filters.language && repo.metadata?.language !== filters.language) {
        return false;
      }

      // Has features filter
      if (filters.hasFeatures) {
        const hasFeatures =
          repo.installedFeatures && Object.keys(repo.installedFeatures).length > 0;
        if (!hasFeatures) {
          return false;
        }
      }

      return true;
    });
  }, [repositories, filters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading repositories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
        <div>
          <h3 className="font-medium text-red-900">Error loading repositories</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RepositoryFilters
        onFilterChange={setFilters}
        availableTags={availableTags}
        availableLanguages={availableLanguages}
      />

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredRepositories.length} of {repositories.length} repositories
        </p>
      </div>

      {/* Repository Grid */}
      {filteredRepositories.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No repositories found matching your filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRepositories.map((repo) => {
            const fullName = repo.fullName || `${repo.owner}/${repo.name}`;
            return (
              <RepositoryCard
                key={fullName}
                repository={repo}
                onSync={onSync}
                onClick={onSelectRepository}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
